/**
 * ClockFacePicker - Analog clock face for time selection
 * Material Design-inspired two-phase picker: select hour, then minute.
 * Supports 12h (single ring) and 24h (dual concentric rings) modes.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

interface ClockFacePickerProps {
  /** Selected hour: 0-23 (24h) or 1-12 (12h) */
  selectedHour: number | null;
  /** Selected minute */
  selectedMinute: number | null;
  /** AM/PM (12h mode only) */
  period: 'AM' | 'PM';
  /** Whether using 12h format */
  is12h: boolean;
  /** Minute step interval */
  step: number;
  onHourSelect: (hour: number) => void;
  onMinuteSelect: (minute: number) => void;
  onPeriodChange: (period: 'AM' | 'PM') => void;
}

// Layout constants
const SIZE = 256;
const CENTER = SIZE / 2;
const OUTER_RADIUS = 100;
const INNER_RADIUS = 66;
const NUMBER_HIT_RADIUS = 20;

// 12h hour numbers starting from 12 at top, clockwise
const HOURS_12H = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
// 24h: outer ring is 12, 1-11; inner ring is 0, 13-23
const HOURS_24H_OUTER = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const HOURS_24H_INNER = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

/** Get x,y position on circle at a given index out of total count */
function getPosition(index: number, count: number, radius: number): { x: number; y: number } {
  const angle = (index / count) * 2 * Math.PI - Math.PI / 2;
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

/** Get angle (in radians, 0 = top/12-o'clock, clockwise) from center to a point */
function getAngleFromCenter(x: number, y: number): number {
  const dx = x - CENTER;
  const dy = y - CENTER;
  // atan2 gives angle where 0 = right, counter-clockwise positive
  // We want 0 = top, clockwise positive
  let angle = Math.atan2(dx, -dy);
  if (angle < 0) angle += 2 * Math.PI;
  return angle;
}

/** Get distance from center */
function getDistanceFromCenter(x: number, y: number): number {
  const dx = x - CENTER;
  const dy = y - CENTER;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Snap an angle to the nearest value in a set of count evenly-spaced positions */
function snapToIndex(angle: number, count: number): number {
  const step = (2 * Math.PI) / count;
  let index = Math.round(angle / step) % count;
  if (index < 0) index += count;
  return index;
}

export function ClockFacePicker({
  selectedHour,
  selectedMinute,
  period,
  is12h,
  step,
  onHourSelect,
  onMinuteSelect,
  onPeriodChange,
}: ClockFacePickerProps) {
  const { t } = useTranslation('common');
  const [mode, setMode] = useState<'hour' | 'minute'>('hour');
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    };
  }, []);

  // Reset mode to 'hour' when picker is mounted (fresh open)
  useEffect(() => {
    setMode('hour');
  }, []);

  // Generate minute positions based on step
  const minuteValues = useMemo(() => {
    const values: number[] = [];
    for (let m = 0; m < 60; m += step) {
      values.push(m);
    }
    return values;
  }, [step]);
  const minuteCount = minuteValues.length;

  /** Convert SVG mouse/touch event to SVG coordinates */
  const getSvgPoint = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    const svg = svgRef.current;
    if (!svg) return { x: CENTER, y: CENTER };
    const rect = svg.getBoundingClientRect();
    const scaleX = SIZE / rect.width;
    const scaleY = SIZE / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  /** Given a pointer position in SVG coords, determine the selected value */
  const getValueFromPoint = useCallback(
    (x: number, y: number): number | null => {
      const dist = getDistanceFromCenter(x, y);
      if (dist < 20) return null; // Too close to center

      const angle = getAngleFromCenter(x, y);

      if (mode === 'hour') {
        if (is12h) {
          const idx = snapToIndex(angle, 12);
          return HOURS_12H[idx];
        } else {
          // 24h: determine inner vs outer ring
          const threshold = (OUTER_RADIUS + INNER_RADIUS) / 2;
          const idx = snapToIndex(angle, 12);
          if (dist > threshold) {
            return HOURS_24H_OUTER[idx];
          } else {
            return HOURS_24H_INNER[idx];
          }
        }
      } else {
        // Minute mode
        const idx = snapToIndex(angle, minuteCount);
        return minuteValues[idx];
      }
    },
    [mode, is12h, minuteCount, minuteValues],
  );

  const handleHourClick = useCallback(
    (h: number) => {
      onHourSelect(h);
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = setTimeout(() => setMode('minute'), 200);
    },
    [onHourSelect],
  );

  const handleMinuteClick = useCallback(
    (m: number) => {
      onMinuteSelect(m);
    },
    [onMinuteSelect],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      e.preventDefault();
      setIsDragging(true);
      svgRef.current?.setPointerCapture?.(e.pointerId);

      const pt = getSvgPoint(e.clientX, e.clientY);
      const val = getValueFromPoint(pt.x, pt.y);
      if (val !== null) {
        if (mode === 'hour') {
          onHourSelect(val);
        } else {
          onMinuteSelect(val);
        }
      }
    },
    [getSvgPoint, getValueFromPoint, mode, onHourSelect, onMinuteSelect],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!isDragging) return;
      const pt = getSvgPoint(e.clientX, e.clientY);
      const val = getValueFromPoint(pt.x, pt.y);
      if (val !== null) {
        if (mode === 'hour') {
          onHourSelect(val);
        } else {
          onMinuteSelect(val);
        }
      }
    },
    [isDragging, getSvgPoint, getValueFromPoint, mode, onHourSelect, onMinuteSelect],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!isDragging) return;
      setIsDragging(false);
      svgRef.current?.releasePointerCapture?.(e.pointerId);

      const pt = getSvgPoint(e.clientX, e.clientY);
      const val = getValueFromPoint(pt.x, pt.y);
      if (val !== null) {
        if (mode === 'hour') {
          onHourSelect(val);
          if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
          transitionTimeoutRef.current = setTimeout(() => setMode('minute'), 200);
        } else {
          onMinuteSelect(val);
        }
      }
    },
    [isDragging, getSvgPoint, getValueFromPoint, mode, onHourSelect, onMinuteSelect],
  );

  /** Compute hand endpoint */
  const getHandPosition = (): { x: number; y: number } | null => {
    if (mode === 'hour') {
      if (selectedHour === null) return null;
      if (is12h) {
        const idx = HOURS_12H.indexOf(selectedHour);
        if (idx === -1) return null;
        return getPosition(idx, 12, OUTER_RADIUS);
      } else {
        const outerIdx = HOURS_24H_OUTER.indexOf(selectedHour);
        if (outerIdx !== -1) return getPosition(outerIdx, 12, OUTER_RADIUS);
        const innerIdx = HOURS_24H_INNER.indexOf(selectedHour);
        if (innerIdx !== -1) return getPosition(innerIdx, 12, INNER_RADIUS);
        return null;
      }
    } else {
      if (selectedMinute === null) return null;
      const idx = minuteValues.indexOf(selectedMinute);
      if (idx === -1) return null;
      return getPosition(idx, minuteCount, OUTER_RADIUS);
    }
  };

  const handPos = getHandPosition();

  /** Format hour for digital display */
  const displayHour = selectedHour !== null
    ? (is12h ? String(selectedHour) : String(selectedHour).padStart(2, '0'))
    : '--';

  const displayMinute = selectedMinute !== null
    ? String(selectedMinute).padStart(2, '0')
    : '--';

  return (
    <div className="flex flex-col items-center gap-2 p-1">
      {/* Digital display header */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={cn(
            'rounded-md px-3 py-1.5 text-2xl font-medium tabular-nums transition-colors',
            mode === 'hour'
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground hover:bg-accent',
          )}
          onClick={() => {
            if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
            setMode('hour');
          }}
          aria-label={t('clockFace.editHours')}
        >
          {displayHour}
        </button>
        <span className="text-2xl font-medium text-muted-foreground">:</span>
        <button
          type="button"
          className={cn(
            'rounded-md px-3 py-1.5 text-2xl font-medium tabular-nums transition-colors',
            mode === 'minute'
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground hover:bg-accent',
          )}
          onClick={() => {
            if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
            setMode('minute');
          }}
          aria-label={t('clockFace.editMinutes')}
        >
          {displayMinute}
        </button>
        {is12h && (
          <div className="ml-2 flex flex-col gap-0.5">
            {(['AM', 'PM'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs font-medium transition-colors',
                  period === p
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent',
                )}
                onClick={() => onPeriodChange(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Clock face */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
        height={SIZE}
        className="cursor-pointer select-none touch-none"
        style={{ maxWidth: '100%' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        role="group"
        aria-label={mode === 'hour' ? 'Select hour' : 'Select minute'}
      >
        {/* Background circle */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={OUTER_RADIUS + 22}
          style={{ fill: 'hsl(var(--muted) / 0.3)' }}
        />

        {/* Clock hand */}
        {handPos && (
          <>
            <line
              x1={CENTER}
              y1={CENTER}
              x2={handPos.x}
              y2={handPos.y}
              style={{ stroke: 'hsl(var(--primary))' }}
              strokeWidth={2}
            />
            {/* Selection bubble behind number */}
            <circle
              cx={handPos.x}
              cy={handPos.y}
              r={NUMBER_HIT_RADIUS}
              style={{ fill: 'hsl(var(--primary))' }}
            />
            {/* Center dot */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={4}
              style={{ fill: 'hsl(var(--primary))' }}
            />
          </>
        )}

        {/* Numbers */}
        {mode === 'hour'
          ? renderHourNumbers({
              is12h,
              selectedHour,
              onSelect: handleHourClick,
            })
          : renderMinuteNumbers({
              minuteValues,
              minuteCount,
              selectedMinute,
              onSelect: handleMinuteClick,
            })}
      </svg>
    </div>
  );
}

/** Render hour numbers for 12h or 24h mode */
function renderHourNumbers({
  is12h,
  selectedHour,
  onSelect,
}: {
  is12h: boolean;
  selectedHour: number | null;
  onSelect: (h: number) => void;
}) {
  if (is12h) {
    return HOURS_12H.map((h, i) => {
      const pos = getPosition(i, 12, OUTER_RADIUS);
      const isSelected = selectedHour === h;
      return (
        <g key={h} onClick={() => onSelect(h)} className="cursor-pointer">
          {/* Invisible hit target */}
          <circle cx={pos.x} cy={pos.y} r={NUMBER_HIT_RADIUS} fill="transparent" />
          <text
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="central"
            className="text-sm font-medium pointer-events-none"
            style={{
              fill: isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
              fontSize: '14px',
            }}
          >
            {h}
          </text>
        </g>
      );
    });
  }

  // 24h mode: dual rings
  return (
    <>
      {HOURS_24H_OUTER.map((h, i) => {
        const pos = getPosition(i, 12, OUTER_RADIUS);
        const isSelected = selectedHour === h;
        return (
          <g key={h} onClick={() => onSelect(h)} className="cursor-pointer">
            <circle cx={pos.x} cy={pos.y} r={NUMBER_HIT_RADIUS} fill="transparent" />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="font-medium pointer-events-none"
              style={{
                fill: isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                fontSize: '14px',
              }}
            >
              {h}
            </text>
          </g>
        );
      })}
      {HOURS_24H_INNER.map((h, i) => {
        const pos = getPosition(i, 12, INNER_RADIUS);
        const isSelected = selectedHour === h;
        return (
          <g key={h} onClick={() => onSelect(h)} className="cursor-pointer">
            <circle cx={pos.x} cy={pos.y} r={NUMBER_HIT_RADIUS - 4} fill="transparent" />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="pointer-events-none"
              style={{
                fill: isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                fontSize: '12px',
              }}
            >
              {h}
            </text>
          </g>
        );
      })}
    </>
  );
}

/** Render minute numbers */
function renderMinuteNumbers({
  minuteValues,
  minuteCount,
  selectedMinute,
  onSelect,
}: {
  minuteValues: number[];
  minuteCount: number;
  selectedMinute: number | null;
  onSelect: (m: number) => void;
}) {
  return minuteValues.map((m, i) => {
    const pos = getPosition(i, minuteCount, OUTER_RADIUS);
    const isSelected = selectedMinute === m;
    // Show label for multiples of 5, otherwise show a dot
    const showLabel = m % 5 === 0;
    return (
      <g key={m} onClick={() => onSelect(m)} className="cursor-pointer">
        <circle cx={pos.x} cy={pos.y} r={NUMBER_HIT_RADIUS} fill="transparent" />
        {showLabel ? (
          <text
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="central"
            className="font-medium pointer-events-none"
            style={{
              fill: isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
              fontSize: '14px',
            }}
          >
            {String(m).padStart(2, '0')}
          </text>
        ) : (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={2}
            className="pointer-events-none"
            style={{
              fill: isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
            }}
          />
        )}
      </g>
    );
  });
}
